import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkles, Send, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const KINDS = [
  { v: "insight", l: "1. Commercial Insight" },
  { v: "case_study", l: "2. Case Study" },
  { v: "trigger", l: "3. Trigger Event" },
  { v: "meeting_ask", l: "4. Meeting Ask" },
  { v: "follow_up", l: "Follow-up" },
  { v: "breakup", l: "Breakup" },
  { v: "nurture", l: "Nurture" },
  { v: "reply", l: "Reply to inbound" },
];

export default function AIDraftModal({ open, onClose, lead, onSaved, defaultKind, defaultExtra, existingEmailId, autoGenerate }) {
  const [kind, setKind] = useState(defaultKind || "insight");
  const [extra, setExtra] = useState(defaultExtra || "");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null); // { email_id, subject, body_text, body_html }

  React.useEffect(() => {
    if (!open) return;
    if (defaultKind) setKind(defaultKind);
    if (defaultExtra !== undefined) setExtra(defaultExtra);
    setDraft(null);
    // If we have an existing email_id (e.g. auto-drafted from a trigger), load it
    if (existingEmailId) {
      (async () => {
        try {
          const { data } = await api.get(`/emails/${existingEmailId}`);
          setDraft(data);
        } catch {
          // fall back to fresh generate
          if (autoGenerate) generate();
        }
      })();
    } else if (autoGenerate && lead) {
      // Eager generate when modal opens (used by Coach for "Review & send" feel)
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultKind, defaultExtra, existingEmailId, autoGenerate]);

  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/ai/draft-email", { lead_id: lead.lead_id, kind, extra_instructions: extra || null });
      setDraft(data);
      toast.success("Draft generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!draft) return;
    setSending(true);
    try {
      const { data } = await api.post(`/emails/${draft.email_id}/send`);
      toast.success(data.simulated ? "Sent (simulated — add Resend key for real send)" : "Email sent via Resend");
      onSaved?.();
      onClose();
      setDraft(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (onClose(), setDraft(null))}>
      <DialogContent className="max-w-2xl" style={{ borderRadius: 2 }} data-testid="draft-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>AI Draft · {lead?.full_name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Label className="overline">Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="mt-1 input-crisp" data-testid="draft-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="overline">Extra instructions (optional)</Label>
              <Input value={extra} onChange={(e) => setExtra(e.target.value)} className="mt-1 input-crisp" placeholder="e.g. mention freight contract renewal cycle" data-testid="draft-extra" />
            </div>
          </div>

          {!draft && (
            <Button onClick={generate} disabled={drafting} data-testid="draft-generate" style={{ borderRadius: 2 }}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {drafting ? "Generating…" : "Generate draft"}
            </Button>
          )}

          {draft && (
            <div className="space-y-3 fade-up">
              <div>
                <Label className="overline">Subject</Label>
                <Input
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="mt-1 input-crisp"
                  data-testid="draft-subject"
                />
              </div>
              <div>
                <Label className="overline">Body</Label>
                <Textarea
                  value={draft.body_text}
                  onChange={(e) => setDraft({ ...draft, body_text: e.target.value })}
                  rows={14}
                  className="mt-1 font-mono text-sm input-crisp"
                  data-testid="draft-body"
                />
              </div>
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={generate} disabled={drafting} data-testid="draft-regenerate" style={{ borderRadius: 2 }}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Regenerate
                </Button>
                <Button onClick={send} disabled={sending} data-testid="draft-send" style={{ borderRadius: 2 }}>
                  <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {sending ? "Sending…" : "Approve & Send"}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
